/**
 * URL-search-param-backed time-travel state (P2.8.c).
 *
 * `useAsOf()` reads `?as_of=` from the current URL; `useAsOfSetter()`
 * returns a setter that updates the URL while preserving other
 * search params. Together they're the source of truth for the
 * AsOfPicker control + the page components that pass the value
 * down to data hooks.
 *
 * Design call: the hooks DON'T auto-thread URL state into useCascade
 * etc. Components opt in by calling `useAsOf()` and passing the
 * value into their data hook explicitly. This keeps Home's
 * CascadeHomeCard live-only by construction -- the home page
 * never reads `?as_of=`, so an as_of URL paste leaves Home alone.
 *
 * URL-state-not-context: react-router's URL is the single source
 * of truth (Q1=A in the P2.8 plan doc). Shareable links + F5 +
 * back button all work for free; the cost is one useSearchParams()
 * call per consuming component.
 */
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/** Current as_of value from the URL, or null when not set. */
export function useAsOf(): string | null {
  const [params] = useSearchParams();
  return params.get("as_of");
}

/**
 * Returns a setter that updates `?as_of=` while preserving every
 * other query param the page is using. Pass null to clear (= return
 * to live state).
 */
export function useAsOfSetter(): (value: string | null) => void {
  const [, setParams] = useSearchParams();
  return useCallback(
    (value) => {
      // Read fresh each call so we don't stomp on params that
      // changed between renders.
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) {
            next.delete("as_of");
          } else {
            next.set("as_of", value);
          }
          return next;
        },
        { replace: false },
      );
    },
    [setParams],
  );
}
